import React, { ReactNode } from 'react';
import { ColumnContent, SortableList } from '../SortableList/SortableList';
import { t } from '../../locales/translate';
import { formatCurrency, formatDate } from '../../util/formatters';

type ProductVariant = {
  id: string;
  description: string | null;
  estimatedAvailability: number;
  associatedWithVoucherCampaign: boolean;
  pricingCategory: string;
  sku: string;
  netPrice: number;
  price: number;
};

export type Item = {
  id: string;
  sku: string;
  artist: string | null;
  name: string | null;
  productVariants: Array<ProductVariant>;
  productType: string | null;
  color: string | null;
  price: number | null;
  estimatedAvailability: number | null;
  categories: Array<string>;
  venue: string | null;
  date: string | null;
  disabled: boolean;
};

interface Props {
  type: 'products' | 'events';
  selectedVariantIds: Array<string>;
  emptyMessage: string;
  disableSelectedItems?: boolean;
  onRowSelect: (variantIds: Array<string> | string) => void;
  onSelectAll: () => void;
  items: Array<Item>;
  actions: ReactNode;
  loading?: boolean;
}

export function ProductEventTable({
  type,
  items,
  disableSelectedItems = false,
  emptyMessage,
  onRowSelect,
  onSelectAll,
  selectedVariantIds,
  actions,
  loading,
}: Props): JSX.Element {
  const translation = t.page.voucherCampaign.discountedProductsAndEvents.table;
  const tableProps: {
    columns: Array<ColumnContent<Item> | null | false>;
    expandableColumns: Array<ColumnContent<ProductVariant>>;
  } = {
    ...(type === 'products'
      ? {
          columns: [
            { label: translation.sku, dataKey: 'sku' },
            { label: translation.artist, dataKey: 'artist' },
            {
              label: translation.categories,
              dataKey: 'categories',
              cellRenderer: (row) => row.categories?.map((node) => node).join(', '),
            },
            { label: translation.productType, dataKey: 'productType' },
            { label: translation.motive, dataKey: 'name' },
            { label: translation.color, dataKey: 'color' },
            {
              label: translation.price,
              dataKey: 'price',
              cellRenderer: (row) => formatCurrency(row.price),
            },
            {
              label: translation.availability,
              dataKey: 'estimatedAvailability',
            },
          ],
          expandableColumns: [
            { label: translation.sku, dataKey: 'sku', width: '114px' },
            { label: translation.description, dataKey: 'description', width: '120px' },
            { label: translation.availability, dataKey: 'estimatedAvailability' },
          ],
        }
      : {
          columns: [
            { label: translation.sku, dataKey: 'sku' },
            { label: translation.artist, dataKey: 'artist' },
            {
              label: translation.event,
              dataKey: 'name',
            },
            { label: translation.venue, dataKey: 'venue' },
            {
              label: translation.date,
              dataKey: 'date',
              cellRenderer: (row) => formatDate(row.date),
            },
          ],
          expandableColumns: [
            { label: translation.sku, dataKey: 'sku' },
            { label: translation.pricingCategory, dataKey: 'pricingCategory' },
            { label: translation.variant, dataKey: 'description' },
            {
              label: translation.price,
              dataKey: 'price',
              cellRenderer: (row) => `${row.netPrice} / ${row.price}`,
            },
          ],
        }),
  };

  return (
    <SortableList
      loading={loading}
      fullWidth
      items={items}
      emptyMessage={emptyMessage}
      multiSelect
      selectedItems={items.filter((item) => item.productVariants.some((node) => selectedVariantIds.includes(node.id)))}
      onRowSelect={(data) => onRowSelect(data.productVariants.map((item) => item.id))}
      onSelectAll={onSelectAll}
      compactExpandableContent
      expandable={(row) => row.productVariants.length > 0}
      columns={tableProps.columns}
      disableSelectForRow={(row) => disableSelectedItems && row.disabled}
      forceCheckForRow={(row) => disableSelectedItems && row.productVariants.length > 0 && row.disabled}
      mutedRow={(row) => disableSelectedItems && (row.productVariants.length === 0 || row.disabled)}
      partiallyCheckedRow={(row) =>
        !row.productVariants.every(
          (variant) => selectedVariantIds.includes(variant.id) || variant.associatedWithVoucherCampaign
        ) &&
        row.productVariants.some(
          (variant) => selectedVariantIds.includes(variant.id) || variant.associatedWithVoucherCampaign
        )
      }
      actions={actions}
      expandedContent={(row) => (
        <SortableList<ProductVariant>
          multiSelect
          items={row.productVariants}
          selectedItems={items
            .flatMap((item) => item.productVariants)
            .filter((item) => selectedVariantIds.includes(item.id))}
          columns={tableProps.expandableColumns}
          onRowSelect={(row) => onRowSelect(row.id)}
          disableSelectForRow={(row) => disableSelectedItems && row.associatedWithVoucherCampaign}
          forceCheckForRow={(row) => disableSelectedItems && row.associatedWithVoucherCampaign}
          mutedRow={(row) => disableSelectedItems && row.associatedWithVoucherCampaign}
        />
      )}
    />
  );
}
